import { get, post, put, del } from '../utils/networking';
import {
  WorkEnvironmentSurvey,
  SurveyTemplateVersion,
  PublicWorkEnvironmentSurvey,
  WorkEnvironmentSurveyProgress,
  WorkEnvironmentRisk,
  Measurement,
  Notified,
  TeamSurveySummary,
  ReminderDelivery,
  RiskAssessmentRequest,
  InsufficientAssessmentRequest,
  DataSourceRequest,
  DiscussionRequest,
  ProposalDiscussions,
  WorkEnvironmentMeasures,
  TeamMeasures,
  ProposalRequest,
  ActionPlanning,
  PlanRequest,
  AreaDecisionType,
  MeasureStatus,
  WorkEnvironmentEvaluation,
  WorkEnvironmentArchive,
  WorkEnvironmentDocumentation,
  ResidualRiskRequest,
  ShareKind,
  TeamPage,
  TeamShares,
  CreateWorkEnvironmentSurveyRequest,
  SubmitWorkEnvironmentSurveyRequest,
} from './workEnvironmentTypes';

const BASE_PATH = '/api/work-environment';

// Survey endpoints
export const createWorkEnvironmentSurvey = async (
  data: CreateWorkEnvironmentSurveyRequest,
): Promise<WorkEnvironmentSurvey> => {
  return await post(`${BASE_PATH}/surveys`, data);
};

export const getWorkEnvironmentSurveysByTeamId = async (
  teamId: number,
): Promise<WorkEnvironmentSurvey[]> => {
  return await get(`${BASE_PATH}/surveys/team/${teamId}`);
};

/** The names of the areas in the language of the questions, when the template has it */
export const getTeamSurveyOverview = async (teamId: number, language: string): Promise<TeamSurveySummary[]> => {
  return await get(`${BASE_PATH}/surveys/team/${teamId}/overview?language=${encodeURIComponent(language)}`);
};

export const getWorkEnvironmentSurveyById = async (
  surveyId: number,
): Promise<WorkEnvironmentSurvey> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}`);
};

export const getWorkEnvironmentSurveyByUUID = async (
  uuid: string,
  language: string,
): Promise<PublicWorkEnvironmentSurvey> => {
  return await get(`${BASE_PATH}/surveys/${uuid}/public?language=${encodeURIComponent(language)}`);
};

export const getWorkEnvironmentSurveyProgress = async (
  surveyId: number,
): Promise<WorkEnvironmentSurveyProgress> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/progress`);
};

export const remindWorkEnvironmentTeam = async (
  surveyId: number,
): Promise<WorkEnvironmentSurveyProgress & ReminderDelivery> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/reminders`, {});
};

export const submitWorkEnvironmentSurvey = async (
  data: SubmitWorkEnvironmentSurveyRequest,
): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/submit`, data);
};

export const deleteWorkEnvironmentSurvey = async (
  surveyId: number,
): Promise<void> => {
  return await del(`${BASE_PATH}/surveys/${surveyId}`);
};

// Template endpoints
export const getAllWorkEnvironmentTemplates = async (): Promise<
  SurveyTemplateVersion[]
> => {
  return await get(`${BASE_PATH}/templates`);
};

// Risk Matrix endpoints
/** In the language of the questions, when the template has it */
export const getWorkEnvironmentRisk = async (
  surveyId: number,
  language: string,
): Promise<WorkEnvironmentRisk> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/risk?language=${encodeURIComponent(language)}`);
};

export type HmsReportPdf = 'work-environment-report' | 'work-environment-inspection-report';

/** The backend makes the PDF, so every part is included whatever is open or filtered on the screen */
export const downloadHmsReportPdf = async (
  key: HmsReportPdf,
  surveyId: number,
  section: 'team' | 'hr' | 'safetyRep',
): Promise<Blob> => {
  const params = new URLSearchParams({ key, surveyId: String(surveyId), section, download: 'true' });
  const response = await fetch(`/api/pdf-export?${params}`, { method: 'GET', credentials: 'include' });
  if (!response.ok) throw new Error('PDF export failed');
  return await response.blob();
};

/** The surveys of the team with results, newest first */
export const getWorkEnvironmentMeasurements = async (surveyId: number): Promise<Measurement[]> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/measurements`);
};

export const saveWorkEnvironmentRiskAssessment = async (
  surveyId: number,
  data: RiskAssessmentRequest,
): Promise<Notified> => {
  return await put(`${BASE_PATH}/surveys/${surveyId}/risk-assessment`, data);
};

export const remindSafetyRepsOfRisk = async (surveyId: number): Promise<ReminderDelivery> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/risk-assessment/reminder`, {});
};

export const extendWorkEnvironmentDeadline = async (surveyId: number, deadline: string): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/deadline-extension`, { deadline });
};

export const saveWorkEnvironmentInsufficientAssessment = async (
  surveyId: number,
  data: InsufficientAssessmentRequest,
): Promise<void> => {
  return await put(`${BASE_PATH}/surveys/${surveyId}/insufficient-assessment`, data);
};

export const addWorkEnvironmentDataSource = async (surveyId: number, data: DataSourceRequest): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/data-sources`, data);
};

export const removeWorkEnvironmentDataSource = async (surveyId: number, id: number): Promise<void> => {
  return await del(`${BASE_PATH}/surveys/${surveyId}/data-sources/${id}`);
};

export const getWorkEnvironmentMeasures = async (
  surveyId: number,
  language: string,
): Promise<WorkEnvironmentMeasures> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/measures?language=${encodeURIComponent(language)}`);
};

export const openWorkEnvironmentProposals = async (surveyId: number): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/measures/open`, {});
};

export const addLeaderProposal = async (surveyId: number, data: ProposalRequest): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/measures/proposals`, data);
};

export const hideProposal = async (surveyId: number, proposalId: number, reason: string): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/measures/proposals/${proposalId}/hide`, { reason });
};

export const getProposalDiscussions = async (surveyId: number): Promise<ProposalDiscussions> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/measures/discussions`);
};

export const addProposalDiscussion = async (surveyId: number, data: DiscussionRequest): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/measures/discussions`, data);
};

export const removeProposalDiscussion = async (surveyId: number, id: number): Promise<void> => {
  return await del(`${BASE_PATH}/surveys/${surveyId}/measures/discussions/${id}`);
};

export const closeProposals = async (surveyId: number): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/measures/proposals/close`, {});
};

export const closeHighlights = async (surveyId: number): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/measures/highlights/close`, {});
};

export const getActionPlanning = async (surveyId: number, language: string): Promise<ActionPlanning> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/action-plan?language=${encodeURIComponent(language)}`);
};

const areaPath = (surveyId: number, factorKey: string) =>
  `${BASE_PATH}/surveys/${surveyId}/action-plan/areas/${encodeURIComponent(factorKey)}`;

const plansPath = (surveyId: number) => `${BASE_PATH}/surveys/${surveyId}/action-plan/plans`;

/** Creates the plan when it has no id yet */
export const saveActionPlan = async (
  surveyId: number,
  planId: number | null,
  data: PlanRequest,
): Promise<{ id: number }> => {
  return planId
    ? await put(`${plansPath(surveyId)}/${planId}`, data)
    : await post(plansPath(surveyId), data);
};

export const approveActionPlan = async (surveyId: number, planId: number, deviation: string | null) => {
  return await post<Notified>(`${plansPath(surveyId)}/${planId}/approve`, { deviation });
};

export const requestPlanReview = async (surveyId: number, planId: number): Promise<Notified> => {
  return await post(`${plansPath(surveyId)}/${planId}/review`, {});
};

export const withdrawPlanReview = async (surveyId: number, planId: number): Promise<void> => {
  return await del(`${plansPath(surveyId)}/${planId}/review`);
};

export const remindAboutPlanReview = async (surveyId: number, planId: number): Promise<ReminderDelivery> => {
  return await post(`${plansPath(surveyId)}/${planId}/review/reminder`, {});
};

export const commentOnPlan = async (surveyId: number, planId: number, text: string): Promise<void> => {
  return await post(`${plansPath(surveyId)}/${planId}/comments`, { text });
};

export const decideArea = async (
  surveyId: number,
  factorKey: string,
  data: { decision: AreaDecisionType; reason: string },
): Promise<void> => {
  return await put(`${areaPath(surveyId, factorKey)}/decision`, data);
};

export const completeActionPlanStep = async (surveyId: number): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/action-plan/complete`, {});
};

export const setMeasureStatus = async (surveyId: number, measureId: number, status: MeasureStatus): Promise<void> => {
  return await put(`${BASE_PATH}/surveys/${surveyId}/action-plan/measures/${measureId}/status`, { status });
};

export const registerPlanReview = async (
  surveyId: number,
  factorKey: string,
  review: { date: string; note: string },
): Promise<void> => {
  return await post(`${areaPath(surveyId, factorKey)}/reviews`, review);
};

export const removePlanReview = async (surveyId: number, factorKey: string, reviewedOn: string): Promise<void> => {
  return await del(`${areaPath(surveyId, factorKey)}/reviews/${reviewedOn}`);
};

/** When the team gets the invitation, within the working hours of the team */
export const startEvaluation = async (
  surveyId: number,
  deadlineInDays: number,
): Promise<{ invitationSendsAt?: string } | undefined> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/evaluation`, { deadlineInDays });
};

/** In the language of the questions, when the template has it */
export const getEvaluation = async (surveyId: number, language: string): Promise<WorkEnvironmentEvaluation> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/evaluation?language=${encodeURIComponent(language)}`);
};

export const getArchive = async (surveyId: number, language: string): Promise<WorkEnvironmentArchive> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/archive?language=${encodeURIComponent(language)}`);
};

export const decideResidualRisk = async (
  surveyId: number,
  factorKey: string,
  data: ResidualRiskRequest,
): Promise<void> => {
  return await put(`${BASE_PATH}/surveys/${surveyId}/residual-risks/${encodeURIComponent(factorKey)}`, data);
};

/** Moves the next survey, or gives it another owner, for every area left to it */
export const changeNextSurvey = async (
  surveyId: number,
  data: { nextSurveyBy: string; ownerId: number | null },
): Promise<void> => {
  return await put(`${BASE_PATH}/surveys/${surveyId}/next-survey`, data);
};

/** Approves the work after the evaluation and archives it */
export const archiveEvaluatedSurvey = async (surveyId: number): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/archive`, {});
};

/** Without a time, the safety representatives turned the notification off and see the request where they sign */
export const askForSignature = async (surveyId: number): Promise<{ sendsAt: string | null }> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/signature`, {});
};

export const withdrawSignatureRequest = async (surveyId: number): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/signature/withdraw`, {});
};

export const registerSignatureDeviation = async (surveyId: number, reason: string): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/signature/deviation`, { reason });
};

export const getDocumentation = async (surveyId: number, language: string): Promise<WorkEnvironmentDocumentation> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/documentation?language=${encodeURIComponent(language)}`);
};

export const getTeamShares = async (surveyId: number): Promise<TeamShares> => {
  return await get(`${BASE_PATH}/surveys/${surveyId}/shares`);
};

export const shareWithTeam = async (surveyId: number, kind: ShareKind): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/shares`, { kind });
};

const PUBLIC_PATH = `${BASE_PATH}/public`;

/** In the language of the questions, when the template has it */
export const getTeamPage = async (uuid: string, language: string): Promise<TeamPage> => {
  return await get(`${PUBLIC_PATH}/surveys/${uuid}/team?language=${encodeURIComponent(language)}`);
};

/** The token only tells one person's picks apart from another's. It is never linked to anyone. */
const voterHeaders = (token: string) => ({ headers: { 'x-voter-token': token } });

export const getTeamMeasures = async (uuid: string, language: string, token: string): Promise<TeamMeasures> => {
  return await get(
    `${PUBLIC_PATH}/surveys/${uuid}/measures?language=${encodeURIComponent(language)}`,
    voterHeaders(token),
  );
};

export const proposeMeasure = async (uuid: string, data: ProposalRequest): Promise<void> => {
  return await post(`${PUBLIC_PATH}/surveys/${uuid}/measures/proposals`, data);
};

export const pickMeasures = async (uuid: string, token: string, proposalIds: number[]): Promise<void> => {
  return await put(`${PUBLIC_PATH}/surveys/${uuid}/measures/picks`, { proposalIds }, voterHeaders(token));
};

/** Too few answered, and the safety representative or the committee has said what they do instead */
export const archiveInsufficientSurvey = async (surveyId: number): Promise<void> => {
  return await post(`${BASE_PATH}/surveys/${surveyId}/insufficient-assessment/archive`, {});
};
